/**
 * База знаний продукта (как в Битрикс24): разделы + статьи + поиск + контекстная помощь.
 *
 * Один источник правды для:
 *  - раздела «База знаний» (/help, /help/[slug]) — навигация, поиск, чтение;
 *  - контекстной панели «?» (HeaderTools) — статья подбирается по маршруту страницы.
 *
 * Контент подробный: пошаговые инструкции, подсказки, частые вопросы, связанные статьи.
 * Каждый новый экран добавляет статью с привязкой `routes`.
 */

export type HelpSection = {
  heading: string;
  /** Абзацы пояснений. */
  body: string[];
  /** Пошаговая инструкция (нумерованный список) — опционально. */
  steps?: string[];
  /** Подсказка/предупреждение (callout) — опционально. */
  tip?: string;
};

/** Скриншот или схема UI внутри статьи БЗ (`/help/...` в public). */
export type HelpImage = {
  /** Путь от корня сайта, напр. `/help/sc-treasury-01-calendar-year.png`. */
  src: string;
  alt: string;
  caption?: string;
};

export type KbFaq = { q: string; a: string };

export type KbCategory = {
  /** Идентификатор категории (стабильный, в URL не участвует). */
  id: string;
  /** Название раздела базы знаний. */
  title: string;
  /** Короткое описание раздела (для плитки). */
  description: string;
};

export type KbArticle = {
  /** Уникальный slug — часть URL /help/[slug]. */
  slug: string;
  /** Категория (KbCategory.id). */
  category: string;
  /** Заголовок статьи. */
  title: string;
  /** Краткое вступление (1–2 фразы). */
  intro?: string;
  /** Тело статьи — разделы. */
  sections: HelpSection[];
  /** Частые вопросы по теме статьи — опционально. */
  faq?: KbFaq[];
  /** Slug'и связанных статей (в дополнение к статьям той же категории). */
  related?: string[];
  /** Когда контент актуализирован (для отображения «Обновлено»). */
  updated?: string;
  /** Маршруты, для которых статья является контекстной помощью (панель «?»). */
  routes?: string[];
  /** Дополнительные ключевые слова для поиска. */
  keywords?: string[];
  /** Показывать в блоке «Популярные статьи» на главной базы знаний. */
  popular?: boolean;
  /** Скриншоты / схемы интерфейса (1–3 на ключевые статьи). */
  images?: HelpImage[];
};

/** Совместимость со старой сигнатурой контекстной панели (+ расширения статьи). */
export type HelpArticle = {
  title: string;
  intro?: string;
  sections: HelpSection[];
  slug?: string;
  faq?: KbFaq[];
  updated?: string;
  images?: HelpImage[];
};

/** Грубая оценка времени чтения статьи (мин), по объёму текста. */
export function readingMinutes(a: { intro?: string; sections: HelpSection[]; faq?: KbFaq[] }): number {
  const words =
    (a.intro ?? "").split(/\s+/).length +
    a.sections.reduce(
      (n, s) =>
        n +
        s.heading.split(/\s+/).length +
        s.body.join(" ").split(/\s+/).length +
        (s.steps ?? []).join(" ").split(/\s+/).length +
        (s.tip ?? "").split(/\s+/).length,
      0,
    ) +
    (a.faq ?? []).reduce((n, f) => n + f.q.split(/\s+/).length + f.a.split(/\s+/).length, 0);
  return Math.max(1, Math.round(words / 140));
}

export const KB_CATEGORIES: KbCategory[] = [
  { id: "getting-started", title: "Начало работы", description: "Вход, роли и доступ, выбор периода и организации, избранное и помощь." },
  { id: "treasury", title: "Казначейство", description: "Заявки на оплату, согласование, платёжный день, платёжный календарь." },
  { id: "procurement", title: "Закупки", description: "Заявки на закупку, заказы поставщикам, приёмка, счета." },
  { id: "contracts", title: "Договора", description: "Заявки на договор, согласование, реестр, доходные/расходные, бюджет/факт." },
  { id: "budget", title: "Бюджетный контроль", description: "Лимиты по ЦФО и статьям, план-факт, перерасходы, импорт из Excel." },
  { id: "reports", title: "Управленческий учёт и отчёты", description: "P&L, ДДС, баланс, БДР, отельный стандарт USALI." },
  { id: "registers", title: "Реестры и данные", description: "Обороты, остатки ТМЦ, задолженность, загрузка из Excel." },
  { id: "integrations", title: "Настройки и интеграции", description: "Пользователи и роли, маршруты согласования, интеграция с 1С, демо-данные." },
];

export const KB_ARTICLES: KbArticle[] = [
  // ── Начало работы ─────────────────────────────────────────────────────────
  {
    slug: "getting-started",
    category: "getting-started",
    title: "Первый вход и обзор интерфейса",
    intro:
      "Как войти в систему, сменить пароль и сориентироваться в интерфейсе за первые 5 минут. Подходит для нового сотрудника любой роли.",
    updated: "Июль 2026",
    routes: ["/", "/dashboard", "/help", "/ai", "/reconciliation", "/system", "/admin"],
    keywords: ["вход", "логин", "пароль", "начало", "интерфейс", "меню", "навигация", "сменить пароль"],
    popular: true,
    related: ["roles-and-access", "period-and-entity", "favorites-and-help", "treasury-calendar"],
    images: [
      {
        src: "/help/sc-help-01-kb-home.png",
        alt: "Главная база знаний: категории и поиск",
        caption: "База знаний (/help): разделы и популярные статьи",
      },
      {
        src: "/help/sc-ops-01-le-switcher.png",
        alt: "Переключатель юрлица в шапке",
        caption: "В шапке — активное юрлицо (фильтр реестров)",
      },
      {
        src: "/help/sc-ops-01-my-tasks.png",
        alt: "Экран «Мои задачи»",
        caption: "Мои задачи — быстрый вход в согласования и работу",
      },
    ],
    sections: [
      {
        heading: "Вход в систему",
        body: [
          "Доступ выдаёт администратор вашей организации: вы получаете адрес системы и учётные данные (e-mail и временный пароль).",
          "При первом входе система попросит сменить пароль — задайте надёжный (от 8 символов, буквы разных регистров и цифры).",
        ],
        steps: [
          "Откройте адрес системы в браузере (рекомендуем Chrome, Edge или Yandex).",
          "Введите e-mail и временный пароль, нажмите «Войти».",
          "Смените пароль по запросу системы.",
          "Проверьте в правом верхнем углу название организации — это активное юрлицо.",
        ],
        tip: "Если забыли пароль — воспользуйтесь ссылкой «Забыли пароль?» на странице входа или обратитесь к администратору организации.",
      },
      {
        heading: "Из чего состоит экран",
        body: [
          "Слева — главное меню по разделам (Казначейство, Закупки, Договора, Бюджетный контроль, Реестры, Отчётность, Настройки). Состав меню зависит от вашей роли и тарифа.",
          "В Казначействе рабочие пункты: заявки, согласование, календарь платежей и «Платёжный день». Пункта «Планирование» в сайдбаре нет — закладка payment-planning ведёт в календарь.",
          "В Отчётности сверху доступен «Обзор KPI» (/kpi). Переключатель юрлица в шапке фильтрует операционные реестры (см. «Период и юрлицо»).",
          "Сверху — заголовок раздела, кнопки ★ (избранное) и ? (помощь), язык, уведомления и юрлицо. На узких экранах реестры ЗР/PO/CR показывают карточки вместо таблицы.",
        ],
      },
      {
        heading: "Что делать дальше",
        body: [
          "Если вы администратор — заведите сотрудников и роли (Настройки → Команда и роли), проверьте юрлица и объекты.",
          "Если данных ещё нет — загрузите их из Excel или подключите 1С; для демонстрации можно разово загрузить демонстрационные данные (Настройки → Демонстрационные данные).",
          "Казначею: сценарий дня — календарь → день → Платёжный день (статьи «Платёжный календарь» и «Платёжный день»).",
        ],
      },
    ],
    faq: [
      { q: "Какой браузер использовать?", a: "Любой современный: Chrome, Edge, Yandex, Firefox, Safari. Internet Explorer не поддерживается." },
      { q: "Не вижу нужный раздел в меню", a: "Состав меню зависит от роли и тарифа. Обратитесь к администратору — права настраиваются в «Настройки → Команда и роли»." },
      { q: "Где «Планирование» платежей?", a: "Пункта в меню нет. Используйте «Календарь платежей» и «Платёжный день»." },
    ],
  },
  {
    slug: "roles-and-access",
    category: "getting-started",
    title: "Роли и доступ сотрудников",
    intro: "Как роли определяют видимые разделы и доступные действия, роль Финансового директора, как создавать кастомные роли.",
    updated: "Июль 2026",
    keywords: ["роли", "права", "доступ", "сотрудники", "rbac", "согласование", "администратор", "финансовый директор"],
    related: ["settings-roles", "getting-started"],
    sections: [
      {
        heading: "Как устроен доступ",
        body: [
          "Доступ строится на ролях: роль — это набор прав (что видно в меню и какие действия разрешены).",
          "Типовые роли: администратор аккаунта, финансовый директор, казначей, закупщик, финансовый контролёр, руководитель ЦФО, наблюдатель.",
        ],
      },
      {
        heading: "Кто что может",
        body: [
          "Создавать заявки и документы — исполнители профильного раздела (казначей, закупщик, инициатор).",
          "Согласовывать (утверждать/отклонять) — обладатели права согласования (обычно руководители и финконтроль).",
          "Настраивать систему (роли, маршруты, интеграции) — администратор аккаунта.",
        ],
        tip: "Уволенного сотрудника лучше деактивировать, а не удалять — так сохранится история его действий в документах.",
      },
      {
        heading: "Финансовый директор (ФД)",
        body: [
          "Роль ФД — верхний этаж финансового согласования. По умолчанию визирует крупные платежи (свыше настраиваемого порога, стандарт 500 000 ₽) и договоры.",
          "Порог настраивается администратором. ФД также имеет доступ к дашборду бюджетного контроля и отчётам П&L/ДДС.",
        ],
      },
      {
        heading: "Кастомные роли",
        body: [
          "Помимо системных, можно создавать собственные роли с точечной матрицей прав (например, «Руководитель F&B» с доступом только к своим ЦФО).",
          "Подробнее — в статье «Пользователи и роли».",
        ],
      },
    ],
    faq: [
      { q: "Сотрудник не может согласовать заявку", a: "Проверьте, что у его роли есть право согласования и что заявка действительно на согласовании, а не в черновике." },
      { q: "Можно ли ограничить доступ по ЦФО?", a: "Да, через кастомную роль. Создайте роль с ограниченным набором прав и назначьте её нужному сотруднику." },
    ],
  },
  {
    slug: "period-and-entity",
    category: "getting-started",
    title: "Выбор периода и переключение организаций",
    intro:
      "Период отчётов и переключатель юрлица в шапке: что фильтруется, опция «Все ЮЛ» и чего фильтр не делает (права доступа).",
    updated: "Июль 2026",
    routes: ["/settings/legal-entities", "/settings/properties", "/settings/account-structure"],
    keywords: [
      "период",
      "месяц",
      "квартал",
      "год",
      "юрлицо",
      "организация",
      "переключатель",
      "все юл",
      "нет данных",
      "формат даты",
      "дд.мм.гг",
    ],
    related: ["getting-started", "treasury-payment-request", "procurement-overview"],
    images: [
      {
        src: "/help/sc-ops-01-le-switcher.png",
        alt: "Переключатель юридического лица в шапке",
        caption: "Юрлицо в шапке: активное ЮЛ или «Все ЮЛ»",
      },
      {
        src: "/help/sc-ops-01-my-tasks.png",
        alt: "Список «Мои задачи» с фильтром по ЮЛ",
        caption: "Фильтр ЮЛ действует на «Мои задачи» и операционные реестры",
      },
    ],
    sections: [
      {
        heading: "Период",
        body: [
          "Вверху отчётных страниц есть выбор периода (месяц, квартал, год). При смене периода данные пересчитываются.",
          "Если экран пуст — первая причина обычно в выбранном периоде: проверьте, что в нём есть данные.",
        ],
      },
      {
        heading: "Юрлицо в шапке (фильтр реестров)",
        body: [
          "Активное юрлицо переключается в шапке справа. Для операционных реестров это не «декор»: фильтр действует на заявки на оплату (ЗРДС), заказы на закупку (PO), заявки на договор (CR) и список «Мои задачи».",
          "В переключателе есть опция «Все ЮЛ» — показать документы по всем юридическим лицам тенанта. По умолчанию — активное ЮЛ.",
          "Формы создания документа по-прежнему требуют конкретное юрлицо: «Все ЮЛ» удобно для просмотра, но не заменяет выбор организации при создании.",
        ],
        tip: "Не видите заявку в реестре? Сначала проверьте ЮЛ в шапке (и «Все ЮЛ»), затем период и статус.",
      },
      {
        heading: "Что фильтр не означает",
        body: [
          "Переключатель ЮЛ — это фильтр данных на экране, а не изоляция прав «пользователь × юрлицо». Отдельного user×LE ACL пока нет: видимость разделов задаётся ролью.",
          "Часть справочников НСИ и служебных экранов может оставаться в масштабе всего тенанта — не ожидайте, что каждый экран режется по ЮЛ одинаково.",
        ],
      },
      {
        heading: "Формат даты в реестрах",
        body: [
          "Даты создания документов во всех реестрах отображаются в компактном формате «дд.мм.гг» (например, 17.07.26). Полная дата с временем — в аудите и логах.",
          "В реестрах закупок и заявок на договор есть колонка «Дата создания» с тем же коротким форматом.",
        ],
      },
    ],
    faq: [
      {
        q: "Экран пустой, хотя данные точно есть",
        a: "Проверьте период и юрлицо в шапке (попробуйте «Все ЮЛ»). Часто документы относятся к другому объекту.",
      },
      {
        q: "Почему год показывается двумя цифрами (26 вместо 2026)?",
        a: "Компактный формат «дд.мм.гг» для реестров. Полный «дд.мм.гггг» остаётся в карточках и PDF.",
      },
      {
        q: "Ограничивает ли переключатель ЮЛ права сотрудника?",
        a: "Нет. Это фильтр списка. Права задаются ролью; изоляции user×LE пока нет.",
      },
    ],
  },
  {
    slug: "favorites-and-help",
    category: "getting-started",
    title: "Избранное, помощь и база знаний",
    intro: "Как закреплять часто используемые экраны и быстро получать инструкции прямо из любого раздела.",
    updated: "Июль 2026",
    keywords: ["избранное", "звезда", "помощь", "подсказка", "база знаний", "вопрос", "knowledge"],
    related: ["getting-started"],
    images: [
      {
        src: "/help/sc-help-01-kb-home.png",
        alt: "Главная страница базы знаний",
        caption: "База знаний: поиск и категории",
      },
    ],
    sections: [
      {
        heading: "★ Избранное",
        body: ["Звёздочка в шапке добавляет текущий экран в избранное для быстрого доступа. Повторное нажатие убирает его."],
      },
      {
        heading: "? Помощь и база знаний",
        body: [
          "Кнопка «?» в шапке открывает боковую панель с подсказкой по текущему разделу (статья подбирается по маршруту).",
          "Из панели можно «Читать статью целиком» или открыть всю базу знаний с поиском.",
          "Публичная витрина тех же статей — /knowledge (для коллег без входа в кабинет, где доступна).",
        ],
        tip: "В базе знаний работает поиск: введите «лимит», «1С», «согласование» — система покажет подходящие статьи.",
      },
    ],
  },

  // ── Казначейство ──────────────────────────────────────────────────────────
  {
    slug: "treasury-payment-request",
    category: "treasury",
    title: "Заявка на расходование денежных средств (ЗРДС)",
    intro:
      "Полный разбор заявки на оплату: какие поля заполнять, как считается НДС и валюта, связанные закупки и автоматическая сумма, период начисления и что происходит после отправки на согласование.",
    updated: "Июль 2026",
    routes: ["/treasury"],
    keywords: ["заявка", "оплата", "зрдс", "платёж", "расход", "ндс", "валюта", "срок оплаты", "контрагент", "связанные закупки", "период замещения", "accrual", "начисление"],
    popular: true,
    related: ["treasury-approvals", "treasury-payment-register", "contracts-overview", "budget-limits"],
    images: [
      {
        src: "/help/sc-treasury-requests.png",
        alt: "Реестр заявок на оплату (ЗРДС)",
        caption: "Казначейство → Заявки на оплату",
      },
    ],
    sections: [
      {
        heading: "Когда нужна заявка",
        body: [
          "Заявка на расходование ДС — это запрос на оплату поставщику или контрагенту, который проходит согласование перед платежом.",
          "Заявка может создаваться вручную или автоматически — например, при приёмке по закупке или из графика платежей по договору.",
        ],
      },
      {
        heading: "Как создать заявку",
        body: ["Заполните основные реквизиты — система проверит обязательные поля перед отправкой."],
        steps: [
          "Откройте «Казначейство → Новая заявка».",
          "Выберите контрагента и статью движения денежных средств (ДДС).",
          "Укажите сумму, ставку НДС, валюту и срок (предельную дату) оплаты.",
          "При оплате по договору — привяжите договор: сумма попадёт в факт исполнения договора.",
          "Укажите ЦФО/проект и назначение платежа, сохраните.",
        ],
        tip: "НДС считается из суммы, уже включающей налог (как в типовой форме 1С): НДС = сумма × ставка / (100 + ставка).",
      },
      {
        heading: "Срочность и приоритет",
        body: [
          "Срочность («срочно», «планово», «по графику») влияет на приоритет заявки в реестре платёжного дня.",
          "Предельная дата оплаты используется для контроля просрочки — заявки с истекающим сроком подсвечиваются.",
        ],
      },
      {
        heading: "Связанные закупки и автосумма",
        body: [
          "Заявку можно привязать к одной или нескольким закупкам — их суммы автоматически подставляются в поле «Сумма» заявки (по кнопке «Подставить в сумму заявки»).",
          "На карточке заявки виден счётчик привязанных закупок и общая сумма по ним — если превышает сумму заявки, система подсветит расхождение.",
        ],
        steps: [
          "На вкладке «Связанные закупки» нажмите «Добавить закупку».",
          "Выберите одну или несколько подходящих закупок по номеру/дате/поставщику.",
          "Нажмите «Подставить в сумму заявки» — сумма заявки станет равной итогу по закупкам.",
        ],
        tip: "Связка нужна, чтобы отследить «за какие закупки этот платёж» — при оплате факт исполнения закупок обновляется автоматически.",
      },
      {
        heading: "Период замещения (accrual) и лимит бюджета",
        body: [
          "Дата платежа (когда деньги уходят) и дата начисления/замещения (accrual — когда затраты попадают в бюджет соответствующего периода) — это разные вещи.",
          "Лимит БДДС списывается по дате начисления, не по дате платежа. Пример: платим 30 июня за услугу за март — лимит займётся в марте, если так указано в accrual-поле.",
          "По умолчанию accrual = дата платежа (то есть текущий месяц). Явное указание нужно только когда затраты относятся к другому периоду.",
        ],
        tip: "Многострочные заявки могут иметь разные accrual-даты в строках — каждая строка резервирует бюджет своего периода независимо.",
      },
      {
        heading: "Что дальше",
        body: [
          "После создания заявка уходит на согласование по настроенному маршруту.",
          "Утверждённые заявки назначают на день («В платёж на день» в реестре заявок) и работают в «Платёжном дне» / календаре — не в устаревшем «Планировании».",
          "Реестр заявок учитывает юрлицо в шапке; если заявки «пропали» — проверьте ЮЛ и «Все ЮЛ».",
        ],
      },
    ],
    faq: [
      { q: "Можно ли оплатить в валюте?", a: "Да. Укажите валюту и курс — сумма автоматически пересчитается в базовую валюту учёта." },
      { q: "Заявка не отправляется на согласование", a: "Проверьте обязательные поля (контрагент, статья, сумма, срок). Система подсветит незаполненное." },
      { q: "Как привязать оплату к договору?", a: "В заявке выберите договор в соответствующем поле — оплата увеличит «факт» по этому договору в его карточке." },
      { q: "Почему лимит закрыт на другой месяц, а не когда я плачу?", a: "Лимит БДДС относится к периоду начисления затрат, а не к дате платежа. Если платите за прошлый период — укажите accrual-дату в поле «Период начисления»." },
      { q: "Как связать заявку с несколькими закупками?", a: "На вкладке «Связанные закупки» добавьте нужные — сумма подставится автоматически по кнопке «Подставить в сумму заявки»." },
      { q: "Не вижу свою заявку в списке", a: "Проверьте переключатель юрлица в шапке (в т.ч. «Все ЮЛ») и фильтры статуса." },
    ],
  },
  {
    slug: "treasury-approvals",
    category: "treasury",
    title: "Согласование заявок на оплату",
    intro:
      "Как визировать заявки: по одной и пакетом на экране «Согласование заявок», вкладка истории, ветвление по ЦФО и email (если SMTP настроен).",
    updated: "Июль 2026",
    routes: ["/treasury/approvals", "/my-tasks"],
    keywords: [
      "согласование",
      "утвердить",
      "отклонить",
      "виза",
      "маршрут",
      "инбокс",
      "пакет",
      "массовое",
      "bulk",
      "ветвление",
      "цфо",
      "email",
      "уведомление",
    ],
    popular: true,
    related: ["treasury-payment-request", "settings-approval-rules", "period-and-entity"],
    images: [
      {
        src: "/help/sc-treasury-05-approvals.png",
        alt: "Экран согласования заявок на оплату",
        caption: "Казначейство → Согласование заявок (инбокс и bulk)",
      },
      {
        src: "/help/sc-ops-01-my-tasks.png",
        alt: "Мои задачи — дополнительный инбокс",
        caption: "«Мои задачи» — дополнительная очередь согласований",
      },
    ],
    sections: [
      {
        heading: "Инбокс согласования",
        body: [
          "Раздел «Казначейство → Согласование заявок» (/treasury/approvals) — основной инбокс заявок «На согласовании», по которым нужно ваше решение.",
          "«Мои задачи» дублирует очередь задач согласования (в т.ч. по другим типам документов) и тоже поддерживает пакетные действия.",
          "Список заявок учитывает фильтр юрлица в шапке (включая «Все ЮЛ»).",
        ],
        steps: [
          "Откройте «Казначейство → Согласование заявок».",
          "Проверьте сумму, контрагента, статью и назначение платежа.",
          "Нажмите «Утвердить» или «Отклонить».",
          "При отклонении укажите причину — она сохранится в истории заявки.",
        ],
      },
      {
        heading: "Пакетное согласование (массово)",
        body: [
          "На экране «Согласование заявок» отметьте чекбоксами нужные строки текущей страницы и нажмите «Утвердить выбранные» или «Отклонить выбранные».",
          "Обработка идёт по очереди с индикатором прогресса; ошибка по одной заявке не останавливает остальные — в конце сводка «успешно / ошибок».",
          "В «Мои задачи» пакетное согласование тоже доступно для отмеченных задач — это дополнение к инбоксу казначейства, а не единственное место bulk.",
        ],
        steps: [
          "Откройте «Казначейство → Согласование заявок».",
          "Отметьте флажки у заявок на текущей странице.",
          "Нажмите «Утвердить выбранные» или «Отклонить выбранные» (при отклонении укажите общую причину, если система запросит).",
          "Дождитесь сводки и при необходимости обработайте ошибки по одной.",
        ],
        tip: "Bulk действует в рамках текущей страницы списка (page-scoped). Смените страницу пагинации, если нужно отметить следующие заявки.",
      },
      {
        heading: "Вкладка «Согласование» на карточке",
        body: [
          "На карточке заявки вкладка «Согласование» показывает историю: этапы, ЦФО, роль, ФИО, статус, дату и комментарий.",
          "Параллельные шаги показаны одним этапом с несколькими строками — заявка идёт дальше, когда все строки получили решение.",
        ],
      },
      {
        heading: "Маршруты, этапы и ветвление по ЦФО",
        body: [
          "Маршрут определяет последовательность согласующих (например, по сумме платежа).",
          "Многошаговый маршрут проводит заявку через несколько ролей; «Утверждена» — после последнего шага.",
          "Многострочная заявка с разными ЦФО разветвляется: каждый Руководитель ЦФО согласует свою строку.",
        ],
        tip: "Маршруты настраиваются в «Настройки → Маршруты согласования». Для bulk нужно право согласования (например, treasury.requests.approve).",
      },
      {
        heading: "Email-уведомления",
        body: [
          "Инициатор получает уведомление при возврате на доработку и при финальном одобрении/отклонении.",
          "Согласующие — при появлении заявки в инбоксе (если включены уведомления в профиле).",
        ],
        tip: "Письма уходят только если на стенде настроен SMTP. Без SMTP уведомления могут писаться в лог, но почта не приходит — уточните у администратора.",
      },
    ],
    faq: [
      { q: "Отклонил по ошибке — что делать?", a: "Инициатор может вернуть заявку в работу, поправить и снова отправить на согласование." },
      {
        q: "Почему у заявки 2 согласующих на одном этапе?",
        a: "Многострочная заявка с разными ЦФО — каждый Руководитель ЦФО согласует свою строку.",
      },
      {
        q: "Пакет остановился на одной задаче?",
        a: "Обработка продолжается по остальным. В конце — сводка; ошибочную заявку согласуйте индивидуально.",
      },
      {
        q: "Где массовое согласование — в Моих задачах или в Согласовании?",
        a: "Основное место для ЗРДС — «Казначейство → Согласование заявок». «Мои задачи» — дополнительный инбокс с пакетными действиями.",
      },
    ],
  },
  {
    slug: "treasury-payment-register",
    category: "treasury",
    title: "Платёжный день и реестр платежей",
    intro:
      "Канонический экран дня оплаты: выбор даты, перенос заявок, печать реестра и скачивание черновика п/п. Связь с календарём и честные границы выгрузки в 1С.",
    updated: "Июль 2026",
    routes: ["/treasury/payment-register", "/reports/payment-registry"],
    keywords: [
      "реестр",
      "платёжный день",
      "печать",
      "оплата",
      "казначей",
      "подпись",
      "перенос",
      "п/п",
      "платёжное поручение",
    ],
    related: ["treasury-calendar", "treasury-payment-request", "treasury-payment-order-1c", "integration-1c"],
    images: [
      {
        src: "/help/sc-treasury-02-payment-register.png",
        alt: "Платёжный день — реестр на выбранную дату",
        caption: "Казначейство → Платёжный день",
      },
      {
        src: "/help/sc-treasury-02-payment-day-dated.png",
        alt: "Платёжный день с датой из календаря",
        caption: "День, открытый из календаря (?planned_date=…)",
      },
    ],
    sections: [
      {
        heading: "Канонический экран",
        body: [
          "Рабочий экран дня — «Казначейство → Платёжный день» (/treasury/payment-register). Здесь утверждённые заявки с выбранной плановой датой.",
          "Старый адрес «Реестр платежей» в отчётах (/reports/payment-registry) перенаправляет сюда — не ищите второй равноправный реестр.",
          "В календарь платежей заходят для обзора года/месяца; операции дня (перенос, печать, п/п) — только здесь.",
        ],
      },
      {
        heading: "Что на экране",
        body: [
          "Выберите дату платёжного дня (date picker) — в таблице заявки с этой плановой датой. Можно открыть день из календаря по ссылке с ?planned_date=.",
          "Доступны: перенос выбранных на другую дату, печать реестра, скачивание черновика платёжных поручений для 1С.",
        ],
      },
      {
        heading: "Печать реестра",
        body: [
          "Печатная форма включает юрлицо, контрагента, назначение, сумму; шапку с датой дня и р/с; блоки подписей (Гендиректор / Казначей / ФД).",
        ],
        steps: [
          "Откройте «Казначейство → Платёжный день» и выберите дату.",
          "Проверьте состав и сумму реестра (при нескольких ЮЛ — колонки/фильтр юрлица).",
          "Нажмите «Печать реестра» и сохраните PDF / распечатайте.",
          "После фактической оплаты отметьте заявку оплаченной (см. раздел ниже).",
        ],
      },
      {
        heading: "Как отметить оплату",
        body: [
          "Основной способ — вкладка «Оплата» на карточке заявки: можно провести полную или частичную оплату (несколько строк п/п).",
          "Дополнительно на этом экране — «Импорт оплат»: массово по выбранным строкам, CSV или сверка списаний из 1С. Не заменяет вкладку «Оплата» для одной заявки и частичных платежей.",
        ],
        tip: "День остаётся каноном печати и черновика п/п; отметка оплаты по умолчанию — на карточке.",
      },
      {
        heading: "Черновик п/п и 1С",
        body: [
          "Кнопка выгрузки готовит файл черновика платёжных поручений для загрузки в 1С на стороне бухгалтерии.",
          "Скачивание файла в Keiront подтверждено; автоматическая загрузка (upload) в 1С с кнопки — отдельный контур, на пилоте может быть ещё не закрыт. Не обещайте «одной кнопкой ушло в банк через 1С», если upload не подтверждён у вас на стенде.",
        ],
        tip: "Подробный playbook — статья «Платёжные поручения: файл и 1С».",
      },
    ],
    faq: [
      {
        q: "Почему в меню Отчётов нет отдельного реестра платежей?",
        a: "Канон перенесён в Казначейство → Платёжный день. Старый URL отчёта редиректит сюда.",
      },
      {
        q: "Файл п/п скачался — значит ли это, что 1С уже приняла платежи?",
        a: "Нет. Это выгрузка файла. Загрузку в 1С выполняет бухгалтер/интеграция на стороне 1С, если она у вас настроена.",
      },
      {
        q: "Где отметить, что заявка оплачена — на дне или в карточке?",
        a: "Основной путь — вкладка «Оплата» на карточке заявки (в том числе частями). Импорт на платёжном дне — дополнительный массовый/CSV/1С-путь.",
      },
    ],
  },
  {
    slug: "treasury-calendar",
    category: "treasury",
    title: "Платёжный календарь и кассовые разрывы",
    intro:
      "Год → месяц → день → Платёжный день: как читать объём платежей и куда ушёл пункт «Планирование».",
    updated: "Июль 2026",
    routes: ["/treasury/calendar", "/treasury/payment-planning", "/treasury/desktop", "/treasury/analytics"],
    keywords: ["календарь", "планирование", "кассовый разрыв", "ликвидность", "поступления", "heatmap", "объём"],
    related: ["treasury-payment-register", "treasury-payment-request"],
    images: [
      {
        src: "/help/sc-treasury-01-calendar-year.png",
        alt: "Платёжный календарь — вид года",
        caption: "Календарь: годовой обзор объёма платежей",
      },
      {
        src: "/help/sc-treasury-01-calendar-month.png",
        alt: "Платёжный календарь — сетка дней месяца",
        caption: "Месяц: дни с цветом объёма → клик в Платёжный день",
      },
    ],
    sections: [
      {
        heading: "Зачем нужен календарь",
        body: [
          "Календарь — обзор плановых сумм заявок по месяцам и дням (аналитика, не печать и не п/п).",
          "Сценарий: вид года → клик месяца (сетка дней с цветами объёма) → клик дня → переход в «Платёжный день» с датой в адресе.",
        ],
        tip: "Назначить заявки на день можно из «Заявки на оплату» массовым «В платёж на день».",
      },
      {
        heading: "Пункта «Планирование» в меню нет",
        body: [
          "В сайдбаре Казначейства нет пункта «Планирование». Рабочие экраны дня — «Календарь платежей» и «Платёжный день».",
          "Старые закладки /treasury/payment-planning открывают календарь (редирект). Не ищите «Планирование» как основной инструмент дня.",
        ],
      },
      {
        heading: "Пункта «АРМ казначея» в меню нет",
        body: [
          "Отдельный пункт «АРМ казначея» скрыт: быстрые переходы «Сегодня к оплате» и «Календарь» — на обзоре Казначейства (/treasury).",
          "Старые закладки /treasury/desktop открывают обзор (редирект). Рабочий экран дня по-прежнему «Платёжный день».",
        ],
      },
      {
        heading: "Цвета объёма",
        body: [
          "На годе легенда объёма — пороги по месяцу; в месячной сетке — пороги по дню. Не путайте легенды при чтении heatmap.",
          "Большой год может загружаться дольше: календарь тянет заявки для агрегации на клиенте.",
        ],
      },
    ],
  },

  // ── Закупки ───────────────────────────────────────────────────────────────
  {
    slug: "procurement-overview",
    category: "procurement",
    title: "Закупки: полный цикл от заявки до оплаты",
    intro:
      "Цикл заявка → заказ → приёмка → счёт → оплата; где править черновик заказа; связанные документы и фильтр ЮЛ.",
    updated: "Июль 2026",
    routes: ["/procurement", "/procurement/requests", "/procurement/new", "/procurement/suppliers", "/procurement/invoices"],
    keywords: ["закупка", "заявка", "заказ", "поставщик", "приёмка", "счёт", "цикл", "черновик"],
    popular: true,
    related: ["procurement-order-draft-edit", "procurement-receiving", "related-documents", "treasury-payment-request"],
    images: [
      {
        src: "/help/sc-proc-01-requests.png",
        alt: "Реестр заявок на закупку",
        caption: "Закупки → Заявки",
      },
      {
        src: "/help/sc-proc-02-po-card.png",
        alt: "Карточка заказа поставщику",
        caption: "Карточка заказа (PO)",
      },
    ],
    sections: [
      {
        heading: "Этапы цикла",
        body: ["Закупка проходит несколько шагов; на каждом документ согласуется и переходит дальше."],
        steps: [
          "Заявка на закупку (/procurement/requests или «Закупки → Заявки») → согласование.",
          "Заказ поставщику (после согласования) — создание через «Новый заказ» (/procurement/new).",
          "Приёмка товаров/услуг (полная или частичная) — реестр GRN.",
          "Счёт от поставщика.",
          "Оплата: заявка на оплату может создаваться при приёмке.",
        ],
        tip: "При приёмке система может создать обязательство к оплате в казначействе — не вводите те же данные повторно.",
      },
      {
        heading: "Черновик и отклонённый заказ — можно править",
        body: [
          "Черновик и отклонённый заказ на закупку редактируются: откройте карточку и исправьте шапку / поля БДР, сохраните — не нужно удалять и создавать заново из‑за ошибки в ЦФО или статье.",
          "Ограничение MVP: строки спецификации (line items) этим сценарием не правятся — меняйте шапку и бюджетные поля.",
        ],
        tip: "Подробные шаги — статья «Редактирование черновика заказа».",
      },
      {
        heading: "Связанные документы",
        body: [
          "На карточках заказа, заявки на договор и приёмки доступна панель связанных документов — цепочка видна без ручного ввода UUID в /procurement/chain.",
        ],
      },
      {
        heading: "Поставщики, счета и ЮЛ",
        body: [
          "Справочник поставщиков и счета — в разделах «Поставщики» и «Счета».",
          "Реестры PO/заявок учитывают юрлицо в шапке («Все ЮЛ» — см. статью про период и ЮЛ).",
        ],
      },
      {
        heading: "Связь с бюджетом",
        body: [
          "Закупка проверяется на лимит ЦФО/статьи: при превышении — предупреждение или доп. согласование по настройке.",
        ],
      },
    ],
    faq: [
      {
        q: "Чем заявка на закупку отличается от заказа?",
        a: "Заявка — запрос потребности и согласование. Заказ — документ поставщику после согласования.",
      },
      {
        q: "Ошибся в ЦФО на черновике — только удалять?",
        a: "Нет. Откройте черновик (или отклонённый) и исправьте поля, затем сохраните.",
      },
    ],
  },
  {
    slug: "procurement-order-draft-edit",
    category: "procurement",
    title: "Редактирование черновика заказа на закупку",
    intro: "Как исправить draft или rejected PO без пересоздания: какие поля правятся, чего нет в MVP.",
    updated: "Июль 2026",
    routes: ["/procurement/orders"],
    keywords: ["заказ", "черновик", "правка", "отклонён", "po", "бдр", "редактировать"],
    related: ["procurement-overview", "related-documents", "budget-limits"],
    images: [
      {
        src: "/help/sc-proc-02-po-card.png",
        alt: "Карточка заказа на закупку (верх)",
        caption: "Карточка PO — шапка и статус",
      },
      {
        src: "/help/sc-proc-02-po-card-bottom.png",
        alt: "Карточка заказа на закупку (низ страницы)",
        caption: "Нижняя часть карточки: спецификация / бюджет",
      },
    ],
    sections: [
      {
        heading: "Когда можно править",
        body: [
          "Статусы черновик и отклонён — карточка заказа открывается на редактирование шапки и бюджетных полей (ЦФО, статьи БДР и т.п. по форме).",
          "После отправки на согласование / в работе обычное правило: правки ограничены статусом — не ожидайте полный edit у утверждённого заказа.",
        ],
      },
      {
        heading: "Как исправить",
        body: ["Откройте заказ из «Закупки → Заказы»."],
        steps: [
          "Найдите заказ в статусе «Черновик» или «Отклонён» (проверьте ЮЛ в шапке).",
          "Откройте карточку — поля шапки и БДР доступны для изменения.",
          "Сохраните изменения.",
          "При необходимости снова отправьте на согласование.",
        ],
        tip: "Строки спецификации (line items) в этом MVP не редактируются через тот же сценарий — планируйте позиции при создании или отдельным процессом.",
      },
      {
        heading: "Связанные документы на карточке",
        body: ["Панель связанных документов показывает заявку, приёмки, счета и оплаты по цепочке — удобно после правок сверить контекст."],
      },
    ],
    faq: [
      {
        q: "Не могу изменить сумму строк заказа",
        a: "MVP правки черновика покрывает шапку/БДР, не line items. Уточните у администратора процесс корректировки спецификации.",
      },
    ],
  },
  {
    slug: "procurement-receiving",
    category: "procurement",
    title: "Приёмка товаров и услуг",
    intro: "Как оформить поступление по заказу, реестр GRN и связанные документы после приёмки.",
    updated: "Июль 2026",
    routes: ["/procurement/grn"],
    keywords: ["приёмка", "поступление", "получение", "акт", "накладная", "частично", "grn"],
    related: ["procurement-overview", "related-documents", "treasury-payment-request"],
    images: [
      {
        src: "/help/sc-proc-05-grn.png",
        alt: "Реестр приёмок (GRN)",
        caption: "Закупки → Приёмки (GRN)",
      },
    ],
    sections: [
      {
        heading: "Как оформить приёмку",
        body: [
          "Откройте заказ или реестр приёмок (GRN) и отметьте фактически принятые позиции. Заказ перейдёт в «Принят» или «Принят частично».",
        ],
        steps: [
          "Откройте нужный заказ в «Закупки → Заказы» или реестр «Приёмки».",
          "Отметьте принятые позиции и количество.",
          "Сохраните приёмку — при настройке сформируется обязательство к оплате.",
          "Обязательство уходит в казначейство как заявка на оплату.",
        ],
        tip: "Частичная поставка: укажите фактическое количество; остаток примите позже отдельной приёмкой.",
      },
      {
        heading: "Пустой реестр GRN",
        body: [
          "На новом пилоте реестр приёмок может быть пуст, пока не созданы приёмки или не выполнен backfill исторических данных — это не обязательно ошибка прав.",
        ],
      },
      {
        heading: "Связанные документы",
        body: ["На карточке приёмки панель связанных документов показывает заказ, счета и оплаты (канон вместо UUID /chain)."],
      },
    ],
  },
  {
    slug: "related-documents",
    category: "procurement",
    title: "Связанные документы на карточке",
    intro: "Панель связанных документов на заказах, заявках на договор и приёмках — канон цепочки (пункт «Цепочка документов» скрыт из меню).",
    updated: "Июль 2026",
    keywords: ["связанные", "цепочка", "related", "документы", "заказ", "договор", "приёмка"],
    related: ["procurement-overview", "procurement-receiving", "contract-requests", "contracts-overview"],
    images: [
      {
        src: "/help/sc-proc-03-related-docs-schema.svg",
        alt: "Схема панели связанных документов на карточке PO",
        caption: "Схема: где искать «Связанные документы» (не скриншот продакшена)",
      },
      {
        src: "/help/sc-proc-02-po-card-bottom.png",
        alt: "Нижняя часть карточки PO — контекст связей",
        caption: "Контекст карточки PO (live); панель связей — на документах с заполненными link’ами",
      },
    ],
    sections: [
      {
        heading: "Где искать",
        body: [
          "На карточках заказа на закупку (PO), заявки на договор (CR) и приёмки (GRN) откройте панель «Связанные документы».",
          "Там видны связанные заявки, заказы, приёмки, счета и оплаты по цепочке.",
        ],
      },
      {
        heading: "Канон вместо /chain",
        body: [
          "Раньше цепочку искали через пункт меню «Цепочка документов» (/procurement/chain) с ручным UUID.",
          "Этот пункт скрыт из сайдбара: канон — панель «Связанные документы» на карточке. Старый URL ведёт на обзор закупок.",
        ],
      },
    ],
  },

  // ── Договора ──────────────────────────────────────────────────────────────
  {
    slug: "contracts-overview",
    category: "contracts",
    title: "Договора: как устроен модуль",
    intro: "Обзор: заявки на договор, реестр, согласование, доходные и расходные договоры, бюджет и факт, экспорт в PDF/CSV.",
    updated: "Июль 2026",
    routes: ["/contracts", "/directories/contracts", "/reports/active-contracts"],
    keywords: ["договор", "контракт", "реестр", "доходный", "расходный", "согласование", "бюджет", "факт", "обзор", "экспорт", "pdf", "csv", "выгрузка"],
    popular: true,
    related: ["contract-requests", "contracts-approvals", "related-documents", "period-and-entity"],
    images: [
      {
        src: "/help/sc-contracts-registry.png",
        alt: "Реестр договоров",
        caption: "Договора → Реестр",
      },
    ],
    sections: [
      {
        heading: "Разделы модуля",
        body: [
          "Обзор — сводка по заявкам и договорам (KPI: всего, на согласовании, активные, истекающие; доходные vs расходные).",
          "Заявки на договор — инициация договора и его согласование до заключения.",
          "Реестр — список договоров с фильтрами по статусу, типу и направлению; учитывает юрлицо в шапке («Все ЮЛ»).",
          "Согласование — отдельные инбоксы для заявок и для договоров.",
          "Канонические URL — `/contracts/*`. Карточка договора: `/contracts/[id]`. Legacy `/procurement/contracts/[id]` редиректит на канон (bookmarks OK).",
        ],
      },
      {
        heading: "Правильный порядок работы",
        body: ["В системе договор не появляется «из воздуха» — он рождается из согласованной заявки на договор."],
        steps: [
          "Создаётся заявка на договор (инициатор описывает потребность).",
          "Заявка согласуется по маршруту.",
          "Из согласованной заявки создаётся сам договор.",
          "Договор исполняется: график платежей, факт оплат, бюджет/факт.",
        ],
      },
      {
        heading: "Бюджет и факт по договору",
        body: [
          "В карточке договора блок «Бюджет/факт» показывает плановую сумму, оплачено (по привязанным заявкам) и остаток.",
          "Факт растёт по мере проведения платежей с привязкой к этому договору.",
        ],
      },
      {
        heading: "Экспорт в PDF и CSV",
        body: [
          "Карточка каждого договора — кнопка «Скачать PDF» экспортирует юридические реквизиты, стороны, суммы, статус и историю согласования одним файлом.",
          "Реестр договоров и реестр заявок на договор — кнопка «Экспорт CSV» выгружает список с применёнными фильтрами (статус, тип, направление, поиск).",
        ],
        steps: [
          "Реестр: `/contracts/registry` → отфильтруйте (например, Активные + Расходные) → «Экспорт CSV».",
          "Карточка: `/contracts/[id]` → «Скачать PDF» в правом верхнем углу.",
          "Аналогично для заявок на договор: `/contracts/requests` → «Экспорт CSV» с фильтрами, карточка заявки → «Скачать PDF».",
        ],
        tip: "CSV-экспорт учитывает ВСЕ применённые на экране фильтры (статус/тип/направление/поиск). Если хотите весь реестр — сначала снимите фильтры.",
      },
    ],
    faq: [
      { q: "Доходный и расходный договор — в чём разница?", a: "Расходный — мы платим контрагенту (закупки, услуги, аренда). Доходный — нам платят (аренда залов, услуги гостям и т.п.). Направление задаётся в заявке и переносится в договор." },
      { q: "В каком формате CSV?", a: "UTF-8 с BOM (открывается в Excel без «крякозябр»), разделитель — запятая. Первая строка — заголовки колонок." },
      { q: "PDF договора можно скачать до подписания?", a: "Да. На черновых и не активированных договорах PDF содержит водяной знак «ЧЕРНОВИК»." },
    ],
  },
  {
    slug: "contract-requests",
    category: "contracts",
    title: "Заявка на договор: инициация и согласование",
    intro:
      "Главное звено договорного процесса: как создать заявку на заключение договора, прикрепить документы, провести её через согласование и превратить в договор.",
    updated: "Июль 2026",
    routes: ["/contracts/requests"],
    keywords: ["заявка на договор", "инициация", "согласование", "создать договор", "планируемый контрагент", "конвертация", "вложения", "прикрепить файл", "бюджет", "резерв", "лимит"],
    popular: true,
    related: ["contracts-overview", "contract-requests-approvals", "settings-approval-rules"],
    sections: [
      {
        heading: "Зачем нужна заявка на договор",
        body: [
          "Заявка на договор фиксирует потребность: с кем, на что и примерно на какую сумму планируется договор.",
          "Сначала согласуется именно заявка — и только после согласования создаётся сам договор. Это исключает заключение несогласованных договоров.",
        ],
      },
      {
        heading: "Как создать заявку",
        body: ["Контрагент может быть «планируемым» — если нового поставщика ещё нет в справочнике, укажите его название и ИНН вручную."],
        steps: [
          "Откройте «Договоры → Заявки на договор → Создать заявку».",
          "Заполните предмет, направление (доходный/расходный), контрагента, тип, ориентировочную сумму.",
          "Укажите ЦФО/проект, статью БДР, сроки, обоснование и срочность.",
          "Сохраните и нажмите «Отправить на согласование».",
        ],
        tip: "Если контрагента ещё нет в справочнике — выберите «Планируемый» и впишите название/ИНН. Реального контрагента выберете при создании договора.",
      },
      {
        heading: "Согласование и контроль бюджета",
        body: [
          "Заявка проходит по маршруту согласования (по умолчанию: Руководитель ЦФО → Финансовый контроль). Если строки разбивки указывают разные ЦФО — согласование разветвляется на параллельные ветки (каждый Руководитель ЦФО согласует свою строку).",
          "На карточке показывается мягкая проверка лимита: процент использования и доступный остаток по статье ЦФО, с предупреждением при превышении.",
          "При отправке расходной заявки на согласование система резервирует БДР-лимит по указанной статье и ЦФО. Резерв освобождается при отклонении заявки или конвертации в договор (переходит в договор).",
        ],
        tip: "Если сумма превышает лимит, но у роли есть право на утверждение перерасхода — заявка всё равно уходит на согласование, но с явной пометкой «превышение лимита».",
      },
      {
        heading: "Прикрепление документов к заявке",
        body: [
          "К заявке на договор можно прикрепить сопровождающие файлы: техзадание, коммерческое предложение поставщика, скан согласованных условий и т. п.",
          "Форматы: PDF, DOCX, XLSX, PNG, JPG. Ограничение размера — рекомендуемо до 5 МБ на файл (для более крупных используйте внешнюю ссылку в поле URL).",
        ],
        steps: [
          "Откройте карточку заявки в статусе «Черновик» или «На согласовании» (если у вас есть право на редактирование).",
          "Перейдите на вкладку «Вложения».",
          "Нажмите «Прикрепить файл» → выберите файл на диске.",
          "Файл сохраняется вместе с заявкой; согласующие увидят его в своей карточке.",
        ],
        tip: "Вложения переносятся в созданный договор (при конвертации заявки в договор) для непрерывности документного следа.",
      },
      {
        heading: "Экспорт заявки в PDF",
        body: [
          "Согласованную заявку можно скачать как печатный документ PDF — с реквизитами, суммами, списком вложений и историей согласования.",
        ],
        steps: [
          "Откройте карточку заявки.",
          "Нажмите «Скачать PDF» в правом верхнем углу.",
          "Файл называется по номеру заявки, например `contract_request_ZD-2026-047.pdf`.",
        ],
        tip: "PDF доступен на любом статусе (черновик, на согласовании, согласована, отклонена). Для чернового статуса на PDF ставится водяной знак «ЧЕРНОВИК».",
      },
      {
        heading: "Создание договора из заявки",
        body: ["После согласования заявки её можно превратить в договор — реквизиты и вложения перенесутся автоматически."],
        steps: [
          "Откройте согласованную заявку (статус «Согласована»).",
          "Нажмите «Создать договор на основании заявки».",
          "Укажите номер договора, контрагента (если был «планируемым»), дату подписания.",
          "Договор создаётся активным; заявка переходит в статус «Договор создан» и ссылается на него.",
        ],
      },
    ],
    faq: [
      { q: "Нужно ли потом отдельно согласовывать договор?", a: "Нет. По умолчанию согласуется заявка; договор создаётся уже зарегистрированным. Повторное согласование используется только для дополнительных соглашений." },
      { q: "Где взять статус заявки?", a: "Статусы: Черновик → На согласовании → Согласована → (Договор создан) или Отклонена. Виден в реестре и в карточке заявки." },
      { q: "Можно ли отредактировать заявку после отклонения?", a: "Да. Отклонённую заявку можно поправить и снова отправить на согласование." },
      { q: "Что происходит с резервом бюджета при отклонении?", a: "Резерв БДР-лимита освобождается автоматически при отклонении. При конвертации в договор резерв переносится с заявки на договор — лимит остаётся занятым до окончания договора." },
      { q: "Какие файлы можно прикреплять?", a: "PDF, DOCX, XLSX, PNG, JPG. Крупные файлы (сверх 5 МБ) лучше положить в облачное хранилище и прикрепить как внешний URL." },
    ],
  },
  {
    slug: "contract-requests-approvals",
    category: "contracts",
    title: "Согласование заявок на договор",
    intro: "Где найти заявки на согласование договора и как их обработать, ветвление по ЦФО, email-уведомления.",
    updated: "Июль 2026",
    routes: ["/contracts/requests/approvals", "/my-tasks"],
    keywords: ["согласование заявок", "договор", "утвердить", "отклонить", "инбокс", "ветвление", "цфо", "email"],
    related: ["contract-requests"],
    sections: [
      {
        heading: "Инбокс заявок",
        body: ["Раздел «Договоры → Согласование заявок» собирает заявки на договор в статусе «На согласовании». Также заявки видны в разделе «Мои задачи» с возможностью пакетной обработки."],
        steps: [
          "Откройте «Договоры → Согласование заявок» или «Мои задачи».",
          "Проверьте предмет, контрагента, сумму и направление.",
          "Нажмите «Утвердить» или «Отклонить» (с указанием причины).",
        ],
        tip: "Действия доступны обладателям права согласования. Без права вы увидите кнопку «Открыть» — для просмотра карточки.",
      },
      {
        heading: "Ветвление по ЦФО и email",
        body: [
          "Если заявка многострочная и строки указывают разные ЦФО — маршрут согласования разветвляется на параллельные ветки (каждый Руководитель ЦФО согласует только свою строку).",
          "Инициатор заявки получает email-уведомление при полном одобрении/отклонении. Согласующие — при появлении задачи в их инбоксе (если SMTP настроен и в профиле включены уведомления).",
        ],
      },
    ],
  },
  {
    slug: "contracts-approvals",
    category: "contracts",
    title: "Согласование договоров",
    intro: "Согласование самого договора (используется, как правило, для дополнительных соглашений и изменений).",
    updated: "Июнь 2026",
    routes: ["/contracts/approvals"],
    keywords: ["согласование договоров", "доп.соглашение", "утвердить", "отклонить"],
    related: ["contracts-overview", "contract-requests"],
    sections: [
      {
        heading: "Когда используется",
        body: [
          "Основной путь — согласование заявки на договор, после чего договор создаётся сразу зарегистрированным.",
          "Согласование самого договора применяется отдельно — например, для дополнительных соглашений и изменений условий.",
        ],
      },
    ],
  },

  // ── Бюджетный контроль ────────────────────────────────────────────────────
  {
    slug: "budget-limits",
    category: "budget",
    title: "Лимиты бюджета по ЦФО и статьям",
    intro:
      "Плановые лимиты БДР/БДДС: импорт из Excel, ручное создание и правка, факт исполнения и удаление с проверкой резерва.",
    updated: "Июль 2026",
    routes: ["/directories/budget-limits"],
    keywords: ["лимит", "цфо", "бюджет", "план", "факт", "исполнение", "импорт", "excel", "период", "бдр", "бддс", "ручной"],
    popular: true,
    related: ["budget-plan-vs-fact", "excel-import"],
    sections: [
      {
        heading: "Что такое лимит",
        body: [
          "Лимит — плановая сумма на пересечении период × ЦФО × статья (× проект при необходимости).",
          "Различайте лимиты БДР (доходы/расходы) и БДДС (движение денег) — в UI отдельные вкладки/разделы под /directories/budget-limits.",
          "По лимиту видно: план, резерв, факт и доступный остаток.",
        ],
      },
      {
        heading: "Ручное создание и правка",
        body: [
          "Лимит можно создать и изменить вручную в карточке/форме раздела лимитов — не только через Excel.",
        ],
        steps: [
          "Откройте «Бюджетный контроль → Лимиты» (БДР или БДДС).",
          "Создайте новый лимит или откройте существующий.",
          "Укажите период, ЦФО, статью и сумму; сохраните.",
        ],
        tip: "Удаление лимита с активным резервом может вернуть конфликт (HTTP 409) — сначала снимите/закройте резервирующие документы.",
      },
      {
        heading: "Импорт лимитов (план)",
        body: ["Массовая загрузка плана — из Excel по шаблону."],
        steps: [
          "Откройте «Лимиты бюджета».",
          "«Импорт лимитов (план)» → скачайте шаблон.",
          "Заполните периоды, ЦФО, статьи и суммы.",
          "Загрузите файл — строки появятся в таблице.",
        ],
        tip: "Не путайте «Импорт лимитов (план)» и «Загрузка факта (исполнение)».",
      },
      {
        heading: "Загрузка факта (исполнение)",
        body: [
          "Блок «Загрузка факта» загружает фактические обороты; обновляются «Факт», «Доступно» и «%».",
        ],
      },
      {
        heading: "Если нет данных за период",
        body: ["Проверьте период вверху. Если плана нет — импортируйте или создайте лимиты вручную."],
      },
    ],
    faq: [
      { q: "Чем «зарезервировано» отличается от «факта»?", a: "Резерв — согласованные, ещё не оплаченные обязательства. Факт — проведённые оплаты/обороты." },
      { q: "Лимит можно задать без ЦФО?", a: "Да — «общий» лимит по статье на уровень выше." },
      { q: "Только Excel?", a: "Нет. Есть ручной CRUD лимитов БДР/БДДС; Excel — для массового плана и факта." },
    ],
  },
  {
    slug: "budget-plan-vs-fact",
    category: "budget",
    title: "План-факт, перерасходы и дашборд бюджетного контроля",
    intro: "Как читать отчёты «План-Факт» и «Перерасходы», дашборд «Бюджетный контроль» с KPI по ЦФО, drill-down до документа.",
    updated: "Июль 2026",
    routes: ["/reports/plan-vs-fact", "/reports/overruns", "/reports/budget-execution", "/reports/limits-usage", "/dashboards/budget-control"],
    keywords: ["план-факт", "перерасход", "превышение", "исполнение бюджета", "отклонение", "дашборд", "kpi", "цфо", "drill-down"],
    related: ["budget-limits"],
    sections: [
      {
        heading: "Как читать «План-Факт»",
        body: ["Отчёт показывает плановые и фактические суммы по статьям и отклонение (в рублях и процентах). Отрицательное отклонение по расходам — экономия, положительное — перерасход."],
      },
      {
        heading: "Где открыть (канон в меню)",
        body: [
          "Первичный пункт сайдбара — раздел «Бюджетный контроль» → «План / Факт» (`/reports/plan-vs-fact`). Дубль в «Отчётности» скрыт.",
          "Закладка и soft-link в центре отчётов `/reports` по-прежнему открывают тот же отчёт.",
        ],
      },
      {
        heading: "Перерасходы",
        body: ["Отчёт «Перерасходы» выделяет статьи и ЦФО, где факт превысил лимит, — для быстрого реагирования и разбора причин."],
        tip: "Регулярно (например, еженедельно) просматривайте «Перерасходы», чтобы ловить отклонения до конца периода, а не постфактум.",
      },
      {
        heading: "Дашборд «Бюджетный контроль»",
        body: [
          "Дашборд `/dashboards/budget-control` — сводка исполнения бюджета в одном экране: KPI-плитки (общий процент использования лимита, число ЦФО с перерасходом, топ-статей по отклонению), горизонтальные бары по ЦФО, тренд помесячно.",
          "Бары показывают полное значение лимита и текущий факт цветом (зелёный — норма, жёлтый — приближается к 100%, красный — перерасход).",
        ],
        steps: [
          "Откройте `/dashboards/budget-control` (доступен если включён модуль «Дашборды»).",
          "Проверьте KPI-плитки — есть ли ЦФО в красном.",
          "Клик по ЦФО в баре → drill-down до статей.",
          "Клик по статье → список документов (заявок, закупок), формирующих факт этого месяца.",
        ],
        tip: "Дашборд обновляется в реальном времени по мере проведения фактов — периодически обновляйте страницу для актуальных данных.",
      },
    ],
    faq: [
      { q: "Что важнее — «План-Факт» или дашборд?", a: "Дашборд — для быстрого обзора «где что горит». «План-Факт» — для детального разбора конкретного отклонения (с колонками бюджет/факт/отклонение по статьям)." },
      { q: "Почему в дашборде цифры отличаются от реестра заявок?", a: "Дашборд учитывает только committed-факты (проведённые платежи). Заявки в статусе «На согласовании» отображаются как reserved и не попадают в факт до оплаты." },
      { q: "Где пункт в меню?", a: "Канон — «Бюджетный контроль» → «План / Факт». В «Отчётности» дубль скрыт (70.7)." },
    ],
  },

  // ── Управленческий учёт и отчёты ──────────────────────────────────────────
  {
    slug: "management-accounting",
    category: "reports",
    title: "Управленческий учёт: сводка и отчёты",
    intro: "Обзор управленческого учёта, отчёты P&L/ДДС и связь с разделом «Обзор KPI» в Отчётности.",
    updated: "Июль 2026",
    routes: [
      "/accounting",
      "/finance",
      "/analytics",
      "/fb",
      "/labor",
      "/owner",
      "/quality",
      "/daily-flash",
      "/finoko",
      "/reports",
    ],
    keywords: ["управленческий учёт", "p&l", "отчёт", "финансы", "баланс", "выручка", "прибыль", "рентабельность", "kpi"],
    related: ["report-kpi-overview", "report-bdr", "report-dds", "usali-standard"],
    sections: [
      {
        heading: "Сводные показатели",
        body: ["На обзорном экране /accounting — ключевые показатели (выручка, расходы, прибыль, рентабельность) и график по месяцам."],
      },
      {
        heading: "Обзор KPI в Отчётности",
        body: [
          "В сайдбаре «Отчётность» сверху — «Обзор KPI» (/kpi) и управленческий учёт; блок USALI свёрнут по умолчанию («USALI (расшир.)»).",
          "Не путайте KPI-обзор с «Бюджетный контроль» (/dashboards/budget-control): первый — hotel/ops метрики, второй — исполнение лимитов.",
        ],
        tip: "Подробнее — статья «Обзор KPI».",
      },
      {
        heading: "Профильные отчёты и модули",
        body: [
          "Отсюда и из меню Отчётности переходят в P&L, ДДС, Баланс, БДР, а также в аналитические экраны finance/analytics/F&B — при наличии тарифа и прав.",
          "Платёжный день живёт в Казначействе, не в «Реестрах отчётов».",
        ],
      },
    ],
  },
  {
    slug: "report-kpi-overview",
    category: "reports",
    title: "Обзор KPI (операционные метрики)",
    intro: "Раздел «Отчётность → Обзор KPI»: набор метрик, навигация по вкладкам и отличие от бюджетного дашборда.",
    updated: "Июль 2026",
    routes: ["/kpi"],
    keywords: ["kpi", "метрики", "отчётность", "обзор", "показатели", "revpar", "goppar"],
    related: ["management-accounting", "usali-standard", "budget-plan-vs-fact"],
    images: [
      {
        src: "/help/sc-kpi-reporting.png",
        alt: "Экран обзора KPI",
        caption: "Отчётность → Обзор KPI",
      },
    ],
    sections: [
      {
        heading: "Где открыть",
        body: [
          "Сайдбар → Отчётность → Обзор KPI (/kpi). Подразделы (тренды, рейтинг, водопад и др.) открываются из этого хаба.",
        ],
        steps: [
          "Откройте «Отчётность → Обзор KPI».",
          "Выберите период и объект при наличии фильтров.",
          "Перейдите во вкладку/подстраницу (trends, ranking, waterfall…), если нужна детализация.",
        ],
      },
      {
        heading: "Чего здесь нет",
        body: [
          "Это не экран согласования платежей и не «Платёжный день». Исполнение бюджета смотрите в Бюджетном контроле / план-факт.",
        ],
      },
    ],
  },
  {
    slug: "report-dds",
    category: "reports",
    title: "Движение денежных средств (ДДС)",
    intro: "Как устроен отчёт ДДС по отельному стандарту: операционная, инвестиционная и финансовая деятельность.",
    updated: "Июнь 2026",
    routes: ["/reports/dds-turnover", "/accounting/cashflow", "/finoko/bdds"],
    keywords: ["ддс", "денежный поток", "поступления", "платежи", "операционная", "инвестиционная", "финансовая", "usali"],
    related: ["management-accounting", "usali-standard"],
    sections: [
      {
        heading: "Структура отчёта",
        body: [
          "Денежный поток разбит на три вида деятельности (отельный стандарт USALI): операционную, инвестиционную и финансовую.",
          "Операционные статьи P&L относятся к операционной деятельности; покупка активов — к инвестиционной; кредиты и дивиденды — к финансовой.",
        ],
        tip: "Если статья отнесена не к тому виду деятельности — это настраивается в классификации статей; обратитесь к администратору.",
      },
    ],
  },
  {
    slug: "report-bdr",
    category: "reports",
    title: "Обороты по БДР",
    intro: "Иерархия статей бюджета доходов и расходов с месячными оборотами и расшифровкой по документам.",
    updated: "Июнь 2026",
    routes: ["/reports/bdr-turnover"],
    keywords: ["бдр", "обороты", "доходы", "расходы", "статьи", "drill-down", "факт", "расшифровка"],
    related: ["budget-limits", "excel-import"],
    sections: [
      {
        heading: "Навигация по таблице",
        body: ["Стрелка слева разворачивает дочерние статьи. Включите «Drill-down» и кликните по строке — откроется расшифровка по документам."],
      },
      {
        heading: "Загрузка факта",
        body: ["В блоке «Загрузка факта из Excel» скачайте шаблон, заполните фактические обороты и загрузите. Это факт (не план); после загрузки обновляется отчёт «План-Факт»."],
      },
    ],
  },
  {
    slug: "usali-standard",
    category: "reports",
    title: "Отельный стандарт USALI",
    intro: "Что такое USALI, зачем он нужен и как применяется в отчётах системы.",
    updated: "Июль 2026",
    routes: ["/usali"],
    keywords: ["usali", "юсали", "отельный стандарт", "schedules", "goppar", "revpar", "департаменты"],
    related: ["report-dds", "management-accounting"],
    sections: [
      {
        heading: "Кратко о стандарте",
        body: [
          "USALI — единая система управленческой отчётности для гостиниц. Доходы и расходы группируются по департаментам (Schedules S1–S13).",
          "Стандарт обеспечивает сопоставимость показателей с отраслевыми бенчмарками (GOPPAR — прибыль на номер, RevPAR — выручка на номер).",
        ],
        tip: "USALI важен, если вы сравниваете эффективность отеля с рынком или внутри сети — все считают по единым правилам.",
      },
      {
        heading: "Где в меню",
        body: [
          "В сайдбаре «Отчётность» блок USALI / P&L по стандарту свёрнут по умолчанию под подзаголовком «USALI (расшир.)». KPI и управленческий учёт остаются сверху.",
          "Раскройте подзаголовок или откройте закладку `/usali` / `/finoko/…` — маршруты не удалялись.",
        ],
      },
    ],
  },

  // ── Реестры и данные ──────────────────────────────────────────────────────
  {
    slug: "registers-hub",
    category: "registers",
    title: "Реестры: где смотреть данные организации",
    intro: "Хаб с табличными данными: обороты, остатки ТМЦ, задолженность, исполнение бюджета, справочники с иерархией — с выгрузкой в Excel/CSV.",
    updated: "Июль 2026",
    routes: ["/registers", "/directories", "/directories/counterparties", "/directories/cost-centers"],
    keywords: ["реестр", "обороты", "остатки", "задолженность", "тмц", "выписка", "excel", "выгрузка", "csv", "иерархия", "папки", "фильтр", "контрагенты", "цфо", "тип контрагента", "нси"],
    popular: true,
    related: ["excel-import", "integration-1c", "treasury-payment-register"],
    sections: [
      {
        heading: "Как пользоваться",
        body: [
          "Откройте нужную карточку реестра или справочник НСИ в «Справочники» — попадёте на экран с данными за период (где применимо).",
          "В каждом реестре есть выгрузка в Excel/CSV (с фильтрами) и, где применимо, детализация по документам.",
          "Платёжный день / реестр платежей дня — это «Казначейство → Платёжный день», не карточка в «Реестрах отчётов».",
          "НСИ (2026-07): в сайдбаре — короткий список частых справочников (КП, ЦФО, р/с, статьи, …). Полный перечень, включая редкие типы и перечисления, — хаб «Все справочники» (`/directories`).",
        ],
      },
      {
        heading: "Иерархия и фильтры в справочниках",
        body: [
          "Справочники ЦФО и Контрагенты поддерживают иерархию (папки). ЦФО организуются по Ф1-Ф5 (уровням центра финансовой ответственности), контрагенты можно группировать по типам (Юрлица, ИП, Физлица, Иностранные, Самозанятые).",
          "Фильтр по типу — быстрые чипы над таблицей: клик по «ИП» покажет только индивидуальных предпринимателей с их счётчиком.",
          "Дата создания записи — в колонке «Дата создания» в компактном формате «дд.мм.гг».",
        ],
        tip: "Тип «Самозанятые» (2026-07) — отдельный тип контрагента с полем «статус НПД подтверждён». Полезно для оформления актов и выплат по договорам ГПХ.",
      },
      {
        heading: "Откуда берутся данные",
        body: [
          "Часть реестров наполняется загрузкой из Excel (выписка банка, остатки ТМЦ, реестр задолженности) — до подключения 1С.",
          "После интеграции с 1С эти данные обновляются автоматически (см. «Ручная и автоматическая синхронизация справочников» в статье про 1С).",
        ],
      },
    ],
    faq: [
      { q: "Как быстро отобрать только ИП или самозанятых?", a: "На странице `/directories/counterparties` — клик по чипу с нужным типом (Юрлица/ИП/Физлица/Иностранные/Самозанятые). Счётчик рядом с чипом показывает число записей." },
      { q: "CSV-экспорт учитывает фильтры?", a: "Да. Выгружается ровно то, что видно на экране с применёнными фильтрами и поиском." },
    ],
  },
  {
    slug: "excel-import",
    category: "registers",
    title: "Загрузка данных из Excel",
    intro: "Excel-мост до подключения 1С: что и куда загружать, и чем шаблон отличается от выгрузки.",
    updated: "Июнь 2026",
    keywords: ["excel", "импорт", "загрузка", "шаблон", "выписка", "остатки", "факт", "выгрузка"],
    related: ["registers-hub", "budget-limits", "integration-1c"],
    sections: [
      {
        heading: "Принцип работы",
        body: ["Везде, где есть блок «Загрузка из Excel», сначала скачайте типовой шаблон, заполните его и загрузите обратно."],
        steps: [
          "Нажмите «Скачать шаблон» в нужном разделе.",
          "Заполните данные, не меняя структуру столбцов шаблона.",
          "Загрузите файл через «Загрузить из Excel».",
          "Проверьте результат в таблице.",
        ],
        tip: "Загрузка факта — это фактические данные (обороты, остатки, выписка), а не план. Не путайте с импортом плановых лимитов.",
      },
      {
        heading: "Где доступно",
        body: ["Факт БДР — в «Обороты по БДР»; лимиты и их исполнение — в «Лимиты бюджета»; выписка банка и остатки ТМЦ — в соответствующих реестрах; справочники (ЦФО, контрагенты, проекты) — импорт и выгрузка шаблона."],
      },
    ],
    faq: [
      { q: "Загрузил файл — данные не появились", a: "Проверьте, что не меняли заголовки столбцов в шаблоне и что выбран правильный период. Система покажет ошибки по строкам, если формат нарушен." },
    ],
  },

  // ── Настройки и интеграции ────────────────────────────────────────────────
  {
    slug: "integration-1c",
    category: "integrations",
    title: "Интеграция с 1С:Бухгалтерией (OData)",
    intro:
      "Подключение OData, реальный sync-now, синхронизация справочников, маппинг счетов; честно: выгрузка п/п ≠ upload в 1С; Contour/PMS не как готовый ETL.",
    updated: "Июль 2026",
    routes: ["/settings/integrations", "/settings/integrations/accounting-1c"],
    keywords: [
      "1с",
      "1c",
      "odata",
      "интеграция",
      "подключение",
      "бухгалтерия",
      "синхронизация",
      "sync-now",
      "ошибка 500",
      "iis",
      "ручная синхронизация",
      "контрагенты",
      "п/п",
      "contour",
    ],
    popular: true,
    images: [
      {
        src: "/help/sc-integ-01-integrations.png",
        alt: "Настройки интеграций, включая 1С",
        caption: "Настройки → Интеграции",
      },
    ],
    sections: [
      {
        heading: "Что даёт интеграция",
        body: [
          "После подключения 1С обороты, остатки и справочники подтягиваются через OData — без ручного Excel для этих потоков.",
          "Раздел «Интеграции» доступен на тарифах Pro и Enterprise.",
        ],
      },
      {
        heading: "Как подключить",
        body: ["Понадобятся адрес OData-сервиса 1С, логин, пароль и имя регистра."],
        steps: [
          "Откройте «Настройки → Интеграции → Подключить 1С:Бухгалтерию».",
          "Укажите адрес OData-сервиса, логин, пароль и имя регистра.",
          "Нажмите «Проверить соединение» — система реально обратится к 1С.",
          "При успехе сохраните и запустите синхронизацию.",
        ],
        tip: "«Проверить соединение» — реальный запрос к вашей 1С.",
      },
      {
        heading: "Ручной OData sync-now",
        body: [
          "Кнопка синхронизации «сейчас» запускает реальный OData pull (не заглушку с текстом «Manual sync completed»).",
          "Смотрите статус и историю синхронизаций: успех/ошибка, trigger=manual при ручном запуске.",
          "Это отдельный контур от «Синхронизировать сейчас» по справочникам в панели directory sync — оба реальны, но разные точки входа.",
        ],
        tip: "Если история пуста или ошибка — не считайте, что «всё OK по кнопке»; разберите лог с администратором 1С.",
      },
      {
        heading: "Синхронизация справочников",
        body: [
          "Панель справочников: контрагенты (+счета), договоры, номенклатура, сотрудники, остатки р/с.",
          "Чекбокс «активна» — расписание; «Синхронизировать сейчас» — ручной триггер с прогрессом и итогом «добавлено / обновлено».",
        ],
        steps: [
          "Откройте `/settings/integrations` → карточка 1С-источника.",
          "Выберите справочник → «Синхронизировать сейчас».",
          "Дождитесь результата (крупная номенклатура — минуты, чанками).",
        ],
      },
      {
        heading: "Маппинг счетов и факт в отчётах",
        body: [
          "После подключения настройте маппинг счетов 1С → USALI и загрузите проводки — иначе факт в P&L не появится.",
        ],
        tip: "Статья «Мастер маппинга счетов 1С → USALI».",
      },
      {
        heading: "Платёжные поручения: файл ≠ upload",
        body: [
          "Из «Платёжного дня» можно скачать черновик п/п (download) для загрузки в 1С вручную или своим контуром.",
          "Автоматическая загрузка файла в 1С с кнопки Keiront на пилоте может быть ещё не подтверждена — не обещайте полный контур «кнопка → банк».",
        ],
        tip: "Подробнее — «Платёжные поручения: файл и 1С».",
      },
      {
        heading: "Что ещё не подключено",
        body: [
          "В меню «Интеграции» рабочий контур — 1С:Бухгалтерия (OData) и hub; PMS / 1С:Отель / ЗУП / iiko / Opera / Logus / TravelLine / TNG свёрнуты в секцию «Скоро» с badge (конфиг ≠ готовый ETL).",
          "Экраны настроек этих коннекторов остаются по прямой ссылке, но не означают обмен данными «из коробки» без отдельного проекта.",
          "Contour.Фокус — отдельно; не считать подключённым ETL на пилоте.",
          "SMTP для писем согласования — отдельная настройка стенда; без неё письма не уходят.",
        ],
      },
      {
        heading: "Если соединение не проходит",
        body: [
          "Проверьте публикацию OData на IIS/Apache и права пользователя 1С.",
          "HTTP 500 чаще всего на стороне 1С/веб-сервера, не Keiront.",
        ],
      },
    ],
    related: ["1c-account-mapping", "treasury-payment-order-1c", "registers-hub", "excel-import"],
    faq: [
      { q: "Безопасно ли вводить пароль 1С?", a: "Да. Пароль хранится в зашифрованном виде и используется только для запросов к вашей 1С." },
      { q: "Sync-now — это заглушка?", a: "Нет. Ручной sync выполняет реальный OData pull; проверяйте историю статусов." },
      { q: "Скачал п/п — 1С уже приняла?", a: "Нет. Это файл. Upload в 1С — отдельный шаг/контур." },
      { q: "Проверка соединения выдаёт ошибку 500", a: "Обычно сторона 1С: публикация OData или права. Передайте админу 1С." },
      { q: "Почему факт не в отчётах после подключения?", a: "Нужны маппинг счетов и загрузка проводок." },
    ],
  },
  {
    slug: "treasury-payment-order-1c",
    category: "integrations",
    title: "Платёжные поручения: файл и 1С",
    intro:
      "Как выгрузить черновик п/п из Платёжного дня и что обещать по загрузке в 1С на пилоте.",
    updated: "Июль 2026",
    keywords: ["п/п", "платёжное поручение", "1с", "выгрузка", "download", "upload", "банк"],
    related: ["treasury-payment-register", "integration-1c"],
    sections: [
      {
        heading: "Выгрузка из Платёжного дня",
        body: [
          "На экране «Казначейство → Платёжный день» сформируйте реестр на дату и скачайте черновик платёжных поручений.",
        ],
        steps: [
          "Откройте Платёжный день, выберите дату и состав заявок.",
          "Нажмите действие выгрузки / скачивания п/п (как на кнопке в UI).",
          "Сохраните файл и передайте бухгалтеру или загрузите в 1С по вашему регламенту.",
        ],
      },
      {
        heading: "Граница ответственности",
        body: [
          "Keiront подтверждает download файла. Подтверждённый автоматический upload в 1С — отдельный статус на пилоте (может быть WAITING:HUMAN).",
          "Не смешивайте с OData sync-now справочников/проводок — это другой канал.",
        ],
      },
    ],
    faq: [
      {
        q: "Можно ли одной кнопкой отправить платежи в банк через 1С?",
        a: "Только если у вас отдельно подтверждён контур upload+банк. Из коробки пилота рассчитывайте на файл + действия в 1С.",
      },
    ],
  },
  {
    slug: "1c-account-mapping",
    category: "integrations",
    title: "Мастер маппинга счетов 1С → USALI",
    intro:
      "Как сопоставить счета вашего плана счетов 1С со строками отельного стандарта USALI, чтобы проводки автоматически складывались в факт БДР/P&L.",
    updated: "Июль 2026",
    routes: ["/settings/mapping"],
    keywords: ["маппинг", "счета", "usali", "план счетов", "автоподбор", "факт", "бдр", "p&l", "1с", "статья"],
    popular: true,
    related: ["integration-1c", "usali-standard", "report-bdr"],
    sections: [
      {
        heading: "Зачем нужен маппинг",
        body: [
          "1С ведёт учёт по своему плану счетов (РСБУ), а управленческая отчётность отеля — по стандарту USALI (по департаментам: номера, F&B, АУП, маркетинг и т.д.).",
          "Маппинг — это правило соответствия: счёт 1С → строка USALI + знак (доход +/расход −). По нему проводки из 1С автоматически превращаются в факт управленческого отчёта.",
        ],
      },
      {
        heading: "Как настроить",
        body: ["Откройте «Настройки → Маппинг счетов 1С → USALI»."],
        steps: [
          "Нажмите «Загрузить план счетов из 1С» — подтянется справочник счетов (только чтение).",
          "Нажмите «Автосопоставить всё» — система проставит подсказки для счетов доходов и расходов.",
          "Проверьте строки с пометкой «проверьте» — при необходимости выберите другую строку USALI или знак.",
          "Нажмите «Сохранить всё» — правила сохранятся, факт автоматически пересчитается.",
        ],
        tip: "Автоподсказки берут департамент по умолчанию (например, зарплата → АУП). Если у вас иначе — поправьте строку USALI вручную; после сохранения факт пересчитается сам.",
      },
      {
        heading: "Что попадает в факт",
        body: [
          "В факт БДР/P&L идут счета доходов и расходов (классы 90, 91, 20, 26, 44, 70, 69 и т.п.).",
          "Балансовые счета (касса, банк, ТМЦ, расчёты) в P&L не входят — их правила сохраняются и работают на этапе денежного потока (ДДС) и остатков.",
        ],
      },
      {
        heading: "Обновление факта",
        body: [
          "Изменили маппинг — факт обновляется автоматически при сохранении, либо кнопкой «Пересчитать факт».",
          "Загрузка проводок за период — кнопкой «Загрузить факт»; данные складываются в отчёт USALI P&L по настроенным правилам.",
        ],
      },
    ],
    faq: [
      { q: "Нужно ли маппить все 493 счёта?", a: "Нет. В факт БДР идут только счета доходов и расходов; их и маппим (автоподбор помогает). Балансовые можно оставить на потом (этап ДДС/остатков)." },
      { q: "Ошибусь в маппинге — как исправить?", a: "Поменяйте строку USALI у счёта и сохраните — факт пересчитается идемпотентно, задвоения не будет." },
      { q: "Что делать с балансовыми счетами (50, 51, 60, 62 и др.)?", a: "На этапе БДР/P&L их маппить не нужно — оставьте без сопоставления. Балансовые используются для формирования отчёта ДДС (движение денежных средств) и остатков ТМЦ, там маппинг настраивается отдельно." },
      { q: "Загрузка большого плана счетов (500+) идёт долго — это нормально?", a: "Да. Загрузка идёт постранично (чанками по 500 записей) с сохранением после каждого чанка — прогресс безопасен и виден на панели. Если 1С работает нестабильно, отсчитайте от 30 сек на первую страницу." },
    ],
  },
  {
    slug: "settings-roles",
    category: "integrations",
    title: "Пользователи и роли",
    intro: "Как добавить сотрудника, назначить роль, создать собственную роль и корректно отключить уволенного.",
    updated: "Июль 2026",
    routes: ["/settings/roles", "/settings/users", "/settings"],
    keywords: ["роли", "права", "пользователи", "доступ", "rbac", "сотрудник", "деактивация", "финансовый директор", "фд", "кастомная роль", "создать роль", "настройки"],
    related: ["roles-and-access", "settings-approval-rules", "period-and-entity"],
    sections: [
      {
        heading: "Добавить сотрудника: два способа",
        body: [
          "Сотрудники живут в разделе «Настройки → Команда». Завести нового можно двумя способами: создать карточку самому или отправить приглашение.",
          "«Создать пользователя» — вы сразу задаёте ФИО, e-mail, роль, юрлица и пароль, и сотрудник может войти немедленно. Это основной способ, когда почта для рассылки не настроена.",
          "«Пригласить пользователя» — система выдаёт ссылку, по которой сотрудник сам задаёт себе пароль. Ссылка действует 72 часа.",
        ],
        steps: [
          "Откройте «Настройки → Команда».",
          "Нажмите «+ Создать пользователя».",
          "Заполните ФИО, e-mail и выберите роль — она определяет видимые разделы и доступные действия.",
          "Отметьте юрлица, с которыми сотрудник работает: без этого он войдёт, но не увидит данных.",
          "Оставьте «Сгенерировать пароль автоматически» или задайте пароль сами.",
          "Оставьте включённой галочку «Требовать смену пароля при первом входе» — тогда сотрудник задаст свой пароль сам, и он не будет известен никому другому.",
          "Сохраните и передайте сотруднику e-mail и пароль. Сгенерированный пароль показывается один раз.",
        ],
        tip: "Уволенных деактивируйте, а не удаляйте — история их действий в документах сохранится.",
      },
      {
        heading: "Пароли: кто и как их меняет",
        body: [
          "Свой пароль сотрудник меняет сам: имя в правом верхнем углу → «Мой профиль» → «Смена пароля». Нужен текущий пароль; после смены сессии на других устройствах завершаются.",
          "Администратор может задать сотруднику новый пароль («Задать пароль» в строке сотрудника) — например, когда человек потерял доступ и ждать почту некогда. Активные сессии сотрудника при этом завершаются.",
          "Кнопка «Ссылка на сброс» — вариант, при котором пароль знает только сам сотрудник: система выдаёт одноразовую ссылку, а пароль он задаёт сам.",
          "Пароль должен быть не короче 8 символов, содержать буквы и цифры и не совпадать с типовыми («qwerty123») или с вашим именем и почтой.",
        ],
        tip: "Если почта для рассылки в организации не настроена, письма со ссылками не уходят — система покажет ссылку на экране, и её нужно передать сотруднику самостоятельно. Настраивается в «Настройки → Уведомления».",
      },
      {
        heading: "Роль «Финансовый директор»",
        body: [
          "Роль ФД по умолчанию согласует крупные платежи (свыше настраиваемого порога, по умолчанию 500 000 ₽) и договоры.",
          "Порог согласования настраивается администратором (переменная окружения `ESCALATION_THRESHOLD_KOPECKS`) — по запросу поднимите заявку на нужное значение.",
        ],
      },
      {
        heading: "Создание собственных ролей",
        body: [
          "Помимо системных ролей (Владелец, Администратор, Инициатор, Согласующий, Финансовый директор, Наблюдатель) можно создавать кастомные роли с точечной матрицей прав.",
        ],
        steps: [
          "Откройте «Настройки → Команда → Роли» → «Создать роль».",
          "Задайте название (например, «Руководитель F&B»), slug генерируется автоматически (транслит).",
          "Выберите набор прав (галочки по разделам: treasury.requests.approve, contracts.write, budget_limits.read и т.д.).",
          "Сохраните → роль доступна для назначения сотрудникам и в маршрутах согласования.",
        ],
        tip: "Кастомная роль автоматически появляется в форме цепочки согласования — можно ставить её как одного из согласующих на конкретных этапах.",
      },
    ],
    faq: [
      { q: "Может ли пользователь иметь несколько ролей?", a: "Нет, на один master_account у пользователя одна роль. Но роль может быть кастомной с любым набором прав из нескольких «шаблонных»." },
      { q: "Что делать при увольнении?", a: "Деактивируйте пользователя (не удаляйте) — история его действий в документах и согласованиях сохранится. При необходимости переоформите его задачи на другого через маршрут согласования." },
      { q: "Сотрудник не получил письмо с приглашением. Что делать?", a: "Скорее всего, в организации не настроена почта для рассылки («Настройки → Уведомления»). Система показывает ссылку прямо на экране после нажатия «Пригласить» — передайте её сотруднику сами. Либо заведите сотрудника через «Создать пользователя» и сообщите ему пароль." },
      { q: "Сотрудник забыл пароль.", a: "Откройте «Настройки → Команда», найдите его в списке и нажмите «Задать пароль» (вы зададите новый и сообщите его) либо «Ссылка на сброс» (пароль задаст он сам по ссылке)." },
      { q: "Где сотруднику сменить свой пароль?", a: "Имя пользователя в правом верхнем углу → «Мой профиль» → «Смена пароля». Потребуется текущий пароль." },
      { q: "Сотрудник вошёл, но разделы пустые и юрлицо не выбирается.", a: "Проверьте, назначены ли ему юрлица: «Настройки → Команда» → «Редактировать» в его строке. Без назначенного юрлица данные не отображаются." },
    ],
  },
  {
    slug: "settings-approval-rules",
    category: "integrations",
    title: "Маршруты согласования",
    intro: "Как настроить этапы визирования заявок и договоров — по сумме, статье или подразделению.",
    updated: "Июнь 2026",
    routes: ["/settings/approval-rules", "/settings/approval-chains"],
    keywords: ["маршрут", "согласование", "этапы", "правила", "лимиты суммы", "цепочка"],
    related: ["treasury-approvals", "contract-requests"],
    sections: [
      {
        heading: "Как устроены маршруты",
        body: [
          "Маршрут описывает последовательность согласующих в зависимости от условий (например, суммы платежа).",
          "Документы (заявки на оплату, заказы, заявки на договор) идут по подходящему маршруту автоматически.",
        ],
      },
      {
        heading: "Многошаговые маршруты",
        body: ["Можно задать несколько шагов по очереди — например, для заявок на договор по умолчанию: Руководитель ЦФО → Финансовый контроль. Документ согласован, когда пройдены все шаги."],
        tip: "Для разных сумм можно сделать разные маршруты: мелкие — один шаг, крупные — с участием финдиректора.",
      },
    ],
  },
  {
    slug: "demo-data",
    category: "integrations",
    title: "Демонстрационные данные",
    intro: "Как одним кликом наполнить пустой аккаунт примерами для показа продукта — и чем это отличается от реальных данных.",
    updated: "Июнь 2026",
    routes: ["/settings/demo-data"],
    keywords: ["демо", "демонстрационные данные", "пример", "пустой аккаунт", "наполнить", "показ"],
    related: ["excel-import", "integration-1c"],
    sections: [
      {
        heading: "Зачем это нужно",
        body: [
          "На новом (пустом) аккаунте витрины ещё без данных. Чтобы показать, как система выглядит с данными, можно разово загрузить демонстрационный набор.",
          "Это демонстрационные, а не реальные данные организации. Для реальной работы загружайте свои данные из Excel или подключайте 1С.",
        ],
      },
      {
        heading: "Как загрузить",
        body: ["Доступно администратору аккаунта."],
        steps: [
          "Откройте «Настройки → Демонстрационные данные».",
          "Посмотрите текущее наполнение витрин.",
          "Нажмите «Загрузить демонстрационные данные» и подтвердите.",
          "Откройте дашборды — они наполнятся примерами.",
        ],
        tip: "Кнопка предназначена для пустого/демо-аккаунта. На аккаунт с реальными данными она добавит примеры поверх — будьте внимательны.",
      },
    ],
    faq: [
      { q: "Реальный клиент случайно увидит демо-данные?", a: "Нет. Демо не загружается автоматически — только по явному нажатию администратором. Новый аккаунт стартует чистым." },
    ],
  },
];

const FALLBACK: HelpArticle = {
  title: "Справка по разделу",
  intro:
    "Для этого экрана пока показана общая справка. Ниже — базовые принципы; полная статья «Первый вход и обзор интерфейса» доступна по кнопке «Читать целиком».",
  slug: "getting-started",
  sections: [
    {
      heading: "Общие принципы",
      body: [
        "Большинство экранов поддерживают выбор периода, выгрузку в Excel и переход в связанные отчёты.",
        "Операционные реестры (ЗРДС, заказы, заявки на договор) учитывают юрлицо в шапке — при пустом списке проверьте «Все ЮЛ».",
        "Если данных нет — проверьте период или загрузите данные через Excel / синхронизацию 1С.",
      ],
    },
    {
      heading: "Нужна помощь?",
      body: [
        "Откройте полную статью или базу знаний (кнопки ниже). Контекстная «?» на каждом экране кабинета подбирает ближайшую статью по маршруту.",
      ],
    },
  ],
};

// Маршрут → статья (для контекстной панели). Самый длинный префикс выигрывает.
const ROUTE_INDEX: { route: string; article: KbArticle }[] = KB_ARTICLES.flatMap((a) =>
  (a.routes ?? []).map((route) => ({ route, article: a })),
).sort((x, y) => y.route.length - x.route.length);

/** Подобрать статью по маршруту: точное совпадение, затем самый длинный префикс. Иначе FALLBACK. */
export function resolveHelpArticle(pathname: string): HelpArticle {
  for (const { route, article } of ROUTE_INDEX) {
    if (pathname === route || pathname.startsWith(`${route}/`)) {
      return {
        title: article.title,
        intro: article.intro,
        sections: article.sections,
        slug: article.slug,
        faq: article.faq,
        updated: article.updated,
        images: article.images,
      };
    }
  }
  return FALLBACK;
}

/** Категории, у которых есть хотя бы одна статья. */
export function listCategories(): KbCategory[] {
  const used = new Set(KB_ARTICLES.map((a) => a.category));
  return KB_CATEGORIES.filter((c) => used.has(c.id));
}

export function articlesByCategory(categoryId: string): KbArticle[] {
  return KB_ARTICLES.filter((a) => a.category === categoryId);
}

export function getArticle(slug: string): KbArticle | undefined {
  return KB_ARTICLES.find((a) => a.slug === slug);
}

/** Все slug'и статей — для generateStaticParams публичной базы знаний. */
export function allArticleSlugs(): string[] {
  return KB_ARTICLES.map((a) => a.slug);
}

export function categoryById(categoryId: string): KbCategory | undefined {
  return KB_CATEGORIES.find((c) => c.id === categoryId);
}

export function popularArticles(): KbArticle[] {
  return KB_ARTICLES.filter((a) => a.popular);
}

/** Клиентский поиск по заголовку, вступлению, разделам (вкл. шаги/подсказки), FAQ и ключевым словам. */
export function searchKb(query: string): KbArticle[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/).filter(Boolean);

  const scored = KB_ARTICLES.map((a) => {
    const hay = [
      a.title,
      a.intro ?? "",
      ...(a.keywords ?? []),
      ...a.sections.flatMap((s) => [s.heading, ...s.body, ...(s.steps ?? []), s.tip ?? ""]),
      ...(a.faq ?? []).flatMap((f) => [f.q, f.a]),
    ]
      .join(" ")
      .toLowerCase();
    let score = 0;
    for (const term of terms) {
      if (!hay.includes(term)) continue;
      score += 1;
      if (a.title.toLowerCase().includes(term)) score += 3;
      if ((a.keywords ?? []).some((k) => k.toLowerCase().includes(term))) score += 2;
    }
    return { a, score };
  })
    .filter((x) => x.score > 0)
    .sort((x, y) => y.score - x.score);

  return scored.map((x) => x.a);
}

/** Связанные статьи: сначала явно указанные в `related`, затем добор из той же категории. */
export function relatedArticles(slug: string, limit = 4): KbArticle[] {
  const current = getArticle(slug);
  if (!current) return [];
  const out: KbArticle[] = [];
  const seen = new Set<string>([slug]);
  for (const rel of current.related ?? []) {
    const a = getArticle(rel);
    if (a && !seen.has(a.slug)) {
      out.push(a);
      seen.add(a.slug);
    }
  }
  for (const a of KB_ARTICLES) {
    if (out.length >= limit) break;
    if (a.category === current.category && !seen.has(a.slug)) {
      out.push(a);
      seen.add(a.slug);
    }
  }
  return out.slice(0, limit);
}
